/**
 * Tags API Funktionen
 * 
 * CRUD für DAM Image Tags und Tag-Verknüpfungen.
 */

import { apiClient } from '../api-client';

// ===========================================
// TYPES
// ===========================================

export interface Tag {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  color: string;
  usage_count: number;
  created_at: string;
  updated_at?: string;
  relation_id?: string; // Bei Abfrage von Bild-/Asset-Tags
}

export interface TagsResponse {
  success: boolean;
  tags: Tag[];
  total?: number;
  query?: string;
}

export interface TagResponse {
  success: boolean;
  tag?: Tag;
  error?: string;
}

// ===========================================
// TAG CRUD
// ===========================================

/**
 * Alle Tags des Workspaces abrufen
 */
export async function getTags(params?: {
  order_by?: 'usage_count' | 'name' | 'created_at';
  limit?: number;
}): Promise<TagsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.order_by) searchParams.set('order_by', params.order_by);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  
  const url = `/api/v1/tags${searchParams.toString() ? `?${searchParams}` : ''}`;
  const response = await apiClient.get(url);
  return response.data;
}

/**
 * Tags durchsuchen (für Autocomplete)
 */
export async function searchTags(query: string, limit = 10): Promise<TagsResponse> {
  const response = await apiClient.get(`/api/v1/tags?q=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
}

/**
 * Neuen Tag erstellen
 */
export async function createTag(name: string, color?: string): Promise<TagResponse> {
  const response = await apiClient.post('/api/v1/tags', { name, color });
  return response.data;
}

/**
 * Tag aktualisieren
 */
export async function updateTag(tagId: string, data: { name?: string; color?: string }): Promise<TagResponse> {
  const response = await apiClient.patch(`/api/v1/tags/${tagId}`, data);
  return response.data;
}

/**
 * Tag löschen
 */
export async function deleteTag(tagId: string): Promise<{ success: boolean; error?: string }> {
  const response = await apiClient.delete(`/api/v1/tags/${tagId}`);
  return response.data;
}

// ===========================================
// IMAGE TAGS
// ===========================================

/**
 * Tags für ein generiertes Bild abrufen
 */
export async function getImageTags(imageId: string): Promise<TagsResponse> {
  const response = await apiClient.get(`/api/v1/tags/images/${imageId}`);
  return response.data;
}

/**
 * Tags zu generiertem Bild hinzufügen
 */
export async function addTagsToImage(imageId: string, tagNames: string[]): Promise<TagsResponse> {
  const response = await apiClient.post(`/api/v1/tags/images/${imageId}`, { tags: tagNames });
  return response.data;
}

/**
 * Tag von generiertem Bild entfernen
 */
export async function removeTagFromImage(imageId: string, tagId: string): Promise<{ success: boolean }> {
  const response = await apiClient.delete(`/api/v1/tags/images/${imageId}/${tagId}`);
  return response.data;
}

// ===========================================
// ASSET TAGS
// ===========================================

/**
 * Tags für ein Produkt-Asset abrufen
 */
export async function getAssetTags(assetId: string): Promise<TagsResponse> {
  const response = await apiClient.get(`/api/v1/tags/assets/${assetId}`);
  return response.data;
}

/**
 * Tags zu Produkt-Asset hinzufügen
 */
export async function addTagsToAsset(assetId: string, tagNames: string[]): Promise<TagsResponse> {
  const response = await apiClient.post(`/api/v1/tags/assets/${assetId}`, { tags: tagNames });
  return response.data;
}

/**
 * Tag von Produkt-Asset entfernen
 */
export async function removeTagFromAsset(assetId: string, tagId: string): Promise<{ success: boolean }> {
  const response = await apiClient.delete(`/api/v1/tags/assets/${assetId}/${tagId}`);
  return response.data;
}

// ===========================================
// BULK OPERATIONS
// ===========================================

/**
 * Mehrere Tags erstellen/abrufen (Bulk)
 */
export async function ensureTags(tagNames: string[]): Promise<TagsResponse> {
  const response = await apiClient.post('/api/v1/tags/ensure', { tags: tagNames });
  return response.data;
}



